import axiosInstance from '../../api/axiosInstance';
import { parseTimestamp } from '../../utils/formatTimestamp';
import { formatDate as formatUserDate, formatDateTime as formatUserDateTime } from '../../utils/dateTimeFormat';

export const DATE_RANGE_ENUM = {
  last7: 1,
  last30: 2,
  last90: 3,
  custom: 4,
};

export const DATE_RANGE_OPTIONS = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

export const SUITE_EXECUTION_TYPE = {
  all: 1,
  plan: 2,
  individual: 3,
};

const DATE_FORMAT_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const MAX_CUSTOM_RANGE_DAYS = 365;
const MIN_ALLOWED_YEAR = 2000;

const QUIET = { skipGlobalToast: true, skipGlobalLoader: true };

export const formatDateToISO = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseISODate = (dateString) => {
  if (!DATE_FORMAT_REGEX.test(dateString || '')) return null;
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
};

const getDaysDifference = (startDate, endDate) => {
  const msPerDay = 24 * 60 * 60 * 1000;
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  return Math.round((end - start) / msPerDay);
};

export const getPresetDateRange = (rangeKey) => {
  const endDate = new Date();
  const startDate = new Date();
  if (rangeKey === 'last7') {
    startDate.setDate(endDate.getDate() - 6);
  } else if (rangeKey === 'last90') {
    startDate.setDate(endDate.getDate() - 89);
  } else {
    startDate.setDate(endDate.getDate() - 29);
  }
  return {
    start_date: formatDateToISO(startDate),
    end_date: formatDateToISO(endDate),
  };
};

export const validateCustomDates = (start, end) => {
  const errors = { startDate: '', endDate: '' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minAllowedDate = new Date(MIN_ALLOWED_YEAR, 0, 1);

  const check = (value, label) => {
    if (!value) return `${label} is required`;
    if (!DATE_FORMAT_REGEX.test(value)) return `${label} must be in YYYY-MM-DD format`;
    const parsed = parseISODate(value);
    if (!parsed) return `${label} is not a valid calendar date`;
    if (parsed > today) return `${label} cannot be in the future`;
    if (parsed < minAllowedDate) return `${label} cannot be earlier than ${MIN_ALLOWED_YEAR}-01-01`;
    return '';
  };

  errors.startDate = check(start, 'Start date');
  errors.endDate = check(end, 'End date');

  const parsedStart = parseISODate(start);
  const parsedEnd = parseISODate(end);
  if (parsedStart && parsedEnd && !errors.startDate && !errors.endDate) {
    if (parsedStart > parsedEnd) {
      errors.endDate = 'End date must be on or after start date';
    } else if (getDaysDifference(parsedStart, parsedEnd) > MAX_CUSTOM_RANGE_DAYS) {
      errors.endDate = `Custom range cannot exceed ${MAX_CUSTOM_RANGE_DAYS} days`;
    }
  }
  return errors;
};

export const isPeriodReady = ({ range, from, to }) => {
  if (range !== 'custom') return true;
  const errors = validateCustomDates(from, to);
  return !errors.startDate && !errors.endDate;
};

export const buildReportPayload = (projectId, { range, from, to }) => {
  const dateRangeValue = DATE_RANGE_ENUM[range] ?? DATE_RANGE_ENUM.last30;
  if (range === 'custom') {
    return {
      project_id: Number(projectId),
      date_range: dateRangeValue,
      start_date: from,
      end_date: to,
    };
  }
  const preset = getPresetDateRange(range);
  return {
    project_id: Number(projectId),
    date_range: dateRangeValue,
    start_date: preset.start_date,
    end_date: preset.end_date,
  };
};

/* ------------------------------------------------------------------ */
/* Formatting                                                          */
/* ------------------------------------------------------------------ */

export const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const formatDuration = (seconds) => {
  if (seconds == null || seconds === '' || Number.isNaN(Number(seconds))) return '—';
  const value = Math.max(0, Number(seconds));
  if (value < 60) return `${value.toFixed(value < 10 ? 1 : 0)}s`;
  const total = Math.round(value);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
};

export const formatPercent = (value, digits = 0) => {
  if (value == null || value === '' || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  return `${digits ? n.toFixed(digits) : Math.round(n)}%`;
};

export const formatSigned = (value, suffix = '', digits = 1) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  const rounded = Number.isInteger(n) ? n : Number(n.toFixed(digits));
  return `${rounded > 0 ? '+' : ''}${rounded}${suffix}`;
};

export const formatDate = (value) => {
  if (!value) return '—';
  const iso = parseISODate(String(value));
  const date = iso || parseTimestamp(value);
  if (!date || Number.isNaN(date.getTime())) return String(value);
  return formatUserDate(date);
};

export const formatDateTime = (value) => {
  const date = parseTimestamp(value);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return formatUserDateTime(date);
};

export const formatPeriodLabel = (payload) => {
  if (!payload?.start_date || !payload?.end_date) return '';
  return `${formatDate(payload.start_date)} – ${formatDate(payload.end_date)}`;
};

export const normalizeStatus = (status) => {
  if (!status) return 'Pending';
  const s = String(status).toUpperCase();
  if (s === 'PASSED' || s === 'PASS') return 'Passed';
  if (s === 'FAILED' || s === 'FAIL') return 'Failed';
  if (s === 'PENDING') return 'Pending';
  if (s === 'SKIPPED') return 'Skipped';
  if (s === 'RUNNING' || s === 'IN_PROGRESS') return 'Running';
  const str = String(status);
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const passRateTone = (rate) => {
  if (rate == null || rate === '' || Number.isNaN(Number(rate))) return 'neutral';
  const v = Number(rate);
  if (v >= 90) return 'success';
  if (v >= 70) return 'warning';
  return 'danger';
};

export const healthLabel = (rate) => {
  const tone = passRateTone(rate);
  if (tone === 'success') return 'Healthy';
  if (tone === 'warning') return 'Needs attention';
  if (tone === 'danger') return 'Critical';
  return 'No data';
};

/* ------------------------------------------------------------------ */
/* Response extraction                                                 */
/* ------------------------------------------------------------------ */

const pickObject = (response, predicate) => {
  const candidates = [
    response,
    response?.data,
    response?.data?.data,
    response?.result,
    response?.result?.data,
  ];
  return candidates.find((item) => item && typeof item === 'object' && predicate(item)) || {};
};

const unwrapReport = (response) =>
  response?.data && !Array.isArray(response.data) ? response.data : response;

/* ------------------------------------------------------------------ */
/* Summary-level endpoints                                             */
/* ------------------------------------------------------------------ */

export async function fetchCardData(payload) {
  const response = await axiosInstance.post('/test_case/get_card_test_case_data', payload, QUIET);
  const api =
    response?.total_test_cases != null
      ? response
      : response?.data?.total_test_cases != null
        ? response.data
        : response?.data || response || {};

  const weekly = api?.weekly_trend || {};
  const labels = weekly.labels || [];
  const trend = labels.map((label, i) => {
    const passed = toNumber(weekly.passed?.[i]);
    const failed = toNumber(weekly.failed?.[i]);
    const pending = toNumber(weekly.pending?.[i]);
    const total = passed + failed + pending;
    return {
      label,
      passed,
      failed,
      pending,
      total,
      passRate: total > 0 ? Number(((passed / total) * 100).toFixed(1)) : null,
    };
  });

  const ta = api?.trend_analysis || null;
  return {
    totalTestCases: toNumber(api?.total_test_cases),
    totalExecuted: toNumber(api?.total_executed),
    notExecuted: toNumber(api?.not_executed),
    passed: toNumber(api?.passed),
    failed: toNumber(api?.failed),
    pending: toNumber(api?.pending),
    passRate: toNumber(api?.pass_rate),
    totalExecutionTime: api?.total_execution_time ?? null,
    averageExecutionTime: api?.average_execution_time ?? null,
    flakiness: api?.flakiness_index ?? null,
    trend,
    trendAnalysis: ta
      ? {
          comparisonPeriod: ta.comparison_period || null,
          passRateTrend: ta.pass_rate_trend ?? null,
          failureRateTrend: ta.failure_rate_trend ?? null,
          testVolumeTrend: ta.test_volume_trend ?? null,
          executionTimeTrend: ta.execution_time_trend ?? null,
        }
      : null,
  };
}

export async function fetchEnvironmentReport(payload) {
  const response = await axiosInstance.post('/test_case/get_environment_report', payload, QUIET);
  const list = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];
  return list.map((env) => ({
    id: env?.environment_id,
    name: env?.environment_name || 'Unknown environment',
    totalTestCases: toNumber(env?.total_test_cases),
    totalExecuted: toNumber(env?.total_executed),
    notExecuted: toNumber(env?.not_executed),
    passed: toNumber(env?.passed),
    failed: toNumber(env?.failed),
    pending: toNumber(env?.pending),
    passRate: toNumber(env?.pass_rate),
    avgResponseTime: toNumber(env?.avg_response_time),
  }));
}

export async function fetchSuiteReport(payload, { page = 1, pageSize = 10, search = '' } = {}) {
  const response = await axiosInstance.post(
    '/test_case/get_test_suite_report',
    { ...payload, page, page_size: pageSize, search: search || '' },
    QUIET
  );
  const api = pickObject(
    response,
    (item) =>
      Array.isArray(item?.test_suites) ||
      item?.total_suites != null ||
      item?.total_count != null ||
      item?.total_records != null
  );
  const suites = Array.isArray(api?.test_suites) ? api.test_suites : [];
  const total = toNumber(api?.total_suites ?? api?.total_count ?? api?.total_records) || suites.length;
  return {
    total,
    rows: suites.map((s) => ({
      id: s?.suite_id,
      name: s?.suite_name || '',
      testCases: toNumber(s?.test_cases),
      passed: toNumber(s?.passed),
      failed: toNumber(s?.failed),
      pending: toNumber(s?.pending),
      passRate: toNumber(s?.pass_rate),
    })),
  };
}

export async function fetchExecutionHistory(payload, { page = 1, pageSize = 10 } = {}) {
  const response = await axiosInstance.post(
    `/test_case/get_execution_report?page=${page}&page_size=${pageSize}`,
    payload,
    QUIET
  );
  const api = pickObject(
    response,
    (item) => Array.isArray(item?.data) || item?.total_count != null || item?.total_pages != null
  );
  const rows = Array.isArray(api?.data) ? api.data : [];
  return {
    total: toNumber(api?.total_count),
    totalPages: toNumber(api?.total_pages, 1),
    rows: rows.map((row, index) => ({
      id: `${row?.execution_date || 'execution'}-${index}`,
      date: row?.execution_date || null,
      totalExecuted: toNumber(row?.total_executed),
      passed: toNumber(row?.passed),
      failed: toNumber(row?.failed),
      pending: toNumber(row?.pending),
      passRate: toNumber(row?.pass_rate),
      avgDuration: row?.avg_duration ?? null,
    })),
  };
}

export async function fetchExecutionSummary(payload) {
  const response = await axiosInstance.post('/report/get_execution_summary_report', payload, QUIET);
  const report = unwrapReport(response) || null;
  if (!report || typeof report !== 'object') return null;
  return {
    results: report.results || null,
    coverage: report.coverage || null,
    health: report.health || null,
    topFailedPlans: Array.isArray(report.top_failed_test_plans) ? report.top_failed_test_plans : [],
    dateRange: report.date_range || null,
  };
}

/* ------------------------------------------------------------------ */
/* Detailed endpoints                                                  */
/* ------------------------------------------------------------------ */

export async function fetchPlanExecutions(payload, { page = 1, pageSize = 10, search = '' } = {}) {
  const response = await axiosInstance.post(
    '/report/get_test_execution_report',
    { ...payload, search: search || '', page, page_size: pageSize },
    QUIET
  );
  return {
    rows: Array.isArray(response?.data) ? response.data : [],
    total: toNumber(response?.total_records),
  };
}

export async function fetchSuiteExecutions(
  payload,
  { page = 1, pageSize = 10, search = '', executionType = 'all' } = {}
) {
  const response = await axiosInstance.post(
    '/report/get_test_suite_execution_report',
    {
      ...payload,
      execution_type: SUITE_EXECUTION_TYPE[executionType] ?? SUITE_EXECUTION_TYPE.all,
      search: search || '',
      page,
      page_size: pageSize,
    },
    QUIET
  );
  const report = unwrapReport(response) || {};
  return {
    summary: report?.summary ?? null,
    chart: Array.isArray(report?.chart) ? report.chart : [],
    rows: Array.isArray(report?.executions) ? report.executions : [],
    total: toNumber(report?.total_records ?? response?.total_records),
  };
}

export async function fetchCaseExecutions(
  payload,
  { page = 1, pageSize = 10, search = '', status = 'all' } = {}
) {
  const body = { ...payload, search: search || '', page, page_size: pageSize };
  if (status === 'Passed' || status === 'Failed') body.status_filter = status;
  const response = await axiosInstance.post('/report/get_test_case_execution_report', body, QUIET);
  const report = unwrapReport(response) || {};
  return {
    summary: report?.summary ?? null,
    chart: Array.isArray(report?.chart) ? report.chart : [],
    rows: Array.isArray(report?.executions) ? report.executions : [],
    total: toNumber(report?.total_records ?? response?.total_records),
  };
}

export async function fetchEnvironmentExecutionReport(payload) {
  const response = await axiosInstance.post('/report/get_environment_execution_report', payload, QUIET);
  const report = unwrapReport(response) || {};
  return {
    summary: report?.summary ?? null,
    chart: Array.isArray(report?.chart) ? report.chart : [],
    environments: Array.isArray(report?.environments) ? report.environments : [],
  };
}

const HISTORY_PAGE_SIZE = 100;
const HISTORY_MAX_PAGES = 10;

/**
 * Pages through test case executions for the period (bounded) so per-test
 * history — repeat failures, recoveries, durations — can be derived.
 */
export async function fetchCaseExecutionHistory(payload) {
  const rows = [];
  let total = 0;
  for (let page = 1; page <= HISTORY_MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await fetchCaseExecutions(payload, { page, pageSize: HISTORY_PAGE_SIZE });
    total = result.total || total;
    rows.push(...result.rows);
    if (result.rows.length < HISTORY_PAGE_SIZE || (total && rows.length >= total)) break;
  }
  return { rows, total: Math.max(total, rows.length), truncated: total > rows.length };
}

/* ------------------------------------------------------------------ */
/* Export                                                              */
/* ------------------------------------------------------------------ */

export async function downloadReportPdf({ kind, payload, projectName }) {
  const endpoint =
    kind === 'summary' ? '/report/export_summary_report' : '/report/export_detailed_report';
  const blobData = await axiosInstance.post(endpoint, payload, {
    responseType: 'blob',
    skipGlobalToast: true,
  });
  const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: 'application/pdf' });

  if (blob.type && blob.type.includes('application/json')) {
    const text = await blob.text();
    let message = 'Failed to export report. Please try again.';
    try {
      const errorData = JSON.parse(text);
      if (errorData?.message) message = errorData.message;
    } catch {
      // keep default message
    }
    throw new Error(message);
  }

  const safeName =
    String(projectName || 'Report')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, ' ')
      .trim() || 'Report';
  const label = { summary: 'Summary', detailed: 'Detailed', failures: 'Failures' }[kind] || 'Detailed';

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${safeName}_${label}_Report.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const testKeyOf = (row) =>
  row?.test_case_id != null ? `id:${row.test_case_id}` : `name:${row?.test_case_name || ''}`;

const timeOf = (value) => {
  const d = parseTimestamp(value);
  return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
};

/**
 * Groups test case execution rows by test and classifies each test within the
 * selected period:
 *   new        – latest run failed and it is the only failure in the period
 *   recurring  – latest run failed and the test failed more than once
 *   recovered  – latest run passed after at least one failure in the period
 *   stable     – no failures in the period
 */
export function buildTestHistory(rows = []) {
  const map = new Map();

  rows.forEach((row) => {
    const key = testKeyOf(row);
    if (!map.has(key)) {
      map.set(key, {
        key,
        testCaseId: row?.test_case_id ?? null,
        name: row?.test_case_name || '—',
        suite: row?.suite_name || null,
        plan: row?.test_plan_name || null,
        runs: [],
      });
    }
    map.get(key).runs.push({
      executionId: row?.execution_id ?? null,
      status: normalizeStatus(row?.status),
      executedAt: row?.executed_at || null,
      time: timeOf(row?.executed_at),
      duration: row?.duration == null ? null : toNumber(row.duration, null),
      environment: row?.environment_name || null,
      suite: row?.suite_name || null,
      plan: row?.test_plan_name || null,
    });
  });

  map.forEach((test) => {
    test.runs.sort((a, b) => b.time - a.time);
    const latest = test.runs[0];
    const previous = test.runs[1];
    const failures = test.runs.filter((r) => r.status === 'Failed');
    const durations = test.runs.map((r) => r.duration).filter((d) => d != null && d > 0);

    let streak = 0;
    for (const run of test.runs) {
      if (run.status !== 'Failed') break;
      streak += 1;
    }

    test.latestStatus = latest?.status || 'Pending';
    test.environment = latest?.environment || null;
    test.suite = latest?.suite || test.suite;
    test.plan = latest?.plan || test.plan;
    test.runCount = test.runs.length;
    test.failureCount = failures.length;
    test.passCount = test.runs.filter((r) => r.status === 'Passed').length;
    test.lastFailedAt = failures[0]?.executedAt || null;
    test.lastFailedTime = failures[0]?.time || 0;
    test.failStreak = streak;
    test.statusChanged = Boolean(
      previous &&
        latest &&
        ['Passed', 'Failed'].includes(previous.status) &&
        ['Passed', 'Failed'].includes(latest.status) &&
        previous.status !== latest.status
    );
    test.avgDuration = durations.length
      ? durations.reduce((a, b) => a + b, 0) / durations.length
      : null;
    test.maxDuration = durations.length ? Math.max(...durations) : null;

    if (test.latestStatus === 'Failed') {
      test.pattern = test.failureCount > 1 ? 'recurring' : 'new';
    } else if (test.latestStatus === 'Passed' && test.failureCount > 0) {
      test.pattern = 'recovered';
    } else {
      test.pattern = 'stable';
    }
  });

  return map;
}

export function summarizeHistory(map) {
  const tests = map ? Array.from(map.values()) : [];
  const failing = tests.filter((t) => t.failureCount > 0);
  return {
    tests,
    uniqueFailed: failing.length,
    newFailures: tests.filter((t) => t.pattern === 'new').length,
    recurring: tests.filter((t) => t.pattern === 'recurring').length,
    recovered: tests.filter((t) => t.pattern === 'recovered').length,
    changedStatus: tests.filter((t) => t.statusChanged).length,
    investigateFirst: [...failing]
      .sort((a, b) => b.failureCount - a.failureCount || b.lastFailedTime - a.lastFailedTime)
      .slice(0, 6),
    persistent: tests
      .filter((t) => t.failureCount >= 2)
      .sort(
        (a, b) =>
          b.failStreak - a.failStreak ||
          b.failureCount / b.runCount - a.failureCount / a.runCount ||
          b.failureCount - a.failureCount
      )
      .slice(0, 6),
    slowest: tests
      .filter((t) => t.avgDuration != null)
      .sort((a, b) => b.avgDuration - a.avgDuration)
      .slice(0, 8),
  };
}

export const PATTERN_META = {
  new: { label: 'New', tone: 'danger', hint: 'First failure in this period' },
  recurring: { label: 'Recurring', tone: 'warning', hint: 'Failed more than once in this period' },
  recovered: { label: 'Recovered', tone: 'success', hint: 'Latest run passed after failing' },
  stable: { label: 'Stable', tone: 'neutral', hint: 'No failures in this period' },
};
